import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Heart } from "lucide-react";
import { useBookmarks } from "../lib/bookmarks";

/**
 * TermCard — magazine editorial row (default) OR flippable grid card
 * when `variant="flip"` is passed. The flip card exposes a 3D perspective
 * flip on hover (desktop) / tap (mobile), revealing benefits + ingredients.
 * A ❤️ bookmark button sits top-right on both variants.
 */
export const TermCard = ({ term, categoryName, variant = "editorial" }) => {
  const { isBookmarked, toggle } = useBookmarks("terms");
  const bookmarked = isBookmarked(term.slug);

  const handleBookmark = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(term.slug, { name: term.name, category_slug: term.category_slug });
  };

  if (variant === "flip") {
    return (
      <FlipCard term={term} categoryName={categoryName}
                bookmarked={bookmarked} onBookmark={handleBookmark} />
    );
  }

  // Editorial row (default — kept for /categorie pages)
  return (
    <Link
      to={`/terme/${term.slug}`}
      className="group block py-8 border-b border-bl-border relative"
      data-testid={`term-card-${term.slug}`}
    >
      <div className="grid md:grid-cols-12 gap-6 items-start">
        <div className="md:col-span-3">
          <span className="overline">{categoryName || "Glossaire"}</span>
        </div>
        <div className="md:col-span-7">
          <h3 className="font-serif text-3xl md:text-4xl text-bl-text leading-tight group-hover:text-bl-dore transition-colors">
            {term.name}
          </h3>
          <p className="mt-3 text-sm md:text-base text-bl-textSoft font-light leading-relaxed max-w-prose">
            {term.short_definition}
          </p>
        </div>
        <div className="md:col-span-2 flex md:justify-end items-center gap-3">
          <BookmarkButton bookmarked={bookmarked} onClick={handleBookmark}
                          testId={`bookmark-term-${term.slug}`} />
          <span className="inline-flex items-center gap-2 text-sm tracking-wide text-bl-text">
            Lire <ArrowRight size={16} strokeWidth={1.4} className="transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
  );
};

/* ---------- Flippable card variant ---------- */
const FlipCard = ({ term, categoryName, bookmarked, onBookmark }) => {
  const [tapped, setTapped] = useState(false); // mobile tap-to-flip

  return (
    <div
      className={`term-flip ${tapped ? "term-flip--flipped" : ""}`}
      data-testid={`term-card-flip-${term.slug}`}
    >
      <div className="term-flip__inner">
        {/* FRONT */}
        <div className="term-flip__face term-flip__front">
          <div className="flex items-start justify-between">
            <span className="overline">{categoryName || "Glossaire"}</span>
            <BookmarkButton bookmarked={bookmarked} onClick={onBookmark}
                            testId={`bookmark-term-${term.slug}`} />
          </div>
          <h3 className="font-serif text-2xl md:text-3xl leading-tight mt-6 text-bl-text">
            {term.name}
          </h3>
          <p className="mt-3 text-sm text-bl-textSoft leading-relaxed line-clamp-3">
            {term.short_definition}
          </p>
          <button
            onClick={() => setTapped(true)}
            className="mt-auto text-xs uppercase tracking-[0.2em] text-bl-mauveDeep flex items-center gap-2 md:hidden"
            data-testid={`term-flip-reveal-${term.slug}`}
          >
            <Sparkles size={12} /> Retourner
          </button>
          <span className="mt-auto text-xs uppercase tracking-[0.2em] text-bl-textSoft hidden md:flex items-center gap-2 group-hover:text-bl-mauveDeep">
            <Sparkles size={12} /> Survole pour découvrir
          </span>
        </div>

        {/* BACK */}
        <div className="term-flip__face term-flip__back">
          <div className="flex items-start justify-between">
            <span className="overline text-white/80">{term.name}</span>
            <button
              onClick={() => setTapped(false)}
              className="text-xs text-white/70 hover:text-white md:hidden"
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>
          {term.benefits?.length > 0 && (
            <div className="mt-4">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                Bénéfices
              </span>
              <ul className="mt-2 space-y-1.5 text-sm text-white">
                {term.benefits.slice(0, 3).map((b) => (
                  <li key={b} className="flex gap-2">
                    <span className="text-bl-dore">—</span> {b}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {term.key_ingredients?.length > 0 && (
            <div className="mt-4">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                Actifs clés
              </span>
              <p className="mt-2 text-xs text-white/90">
                {term.key_ingredients.slice(0, 4).join(" · ")}
              </p>
            </div>
          )}
          <Link
            to={`/terme/${term.slug}`}
            className="mt-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white border-b border-white/40 hover:border-white pb-1 self-start"
            data-testid={`term-flip-read-${term.slug}`}
          >
            Lire la fiche <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
};

/* ---------- Bookmark heart button ---------- */
export const BookmarkButton = ({ bookmarked, onClick, testId, light = false }) => (
  <button
    onClick={onClick}
    aria-label={bookmarked ? "Retirer des favoris" : "Ajouter aux favoris"}
    aria-pressed={bookmarked}
    data-testid={testId}
    className={`bookmark-btn ${bookmarked ? "bookmark-btn--active" : ""} ${light ? "bookmark-btn--light" : ""}`}
    type="button"
  >
    <Heart
      size={16}
      strokeWidth={1.6}
      fill={bookmarked ? "currentColor" : "none"}
    />
  </button>
);
